import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  LocalizationProvider,
  DateRangePicker,
  DateRangePickerProps as MuiDateRangePickerProps,
} from '@mui/x-date-pickers-pro';
import dayjs, { Dayjs } from 'dayjs';
import { SingleInputDateRangeField } from '@mui/x-date-pickers-pro';
import { useCallback, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  debounce,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { t } from 'i18next';
import { sgrColors } from '../../ui/sgr/SgrColors';

interface DateRangeShortcut {
  id: string;
  label: string;
  range: [Dayjs, Dayjs];
}

const buildDateRangeShortcuts = (): DateRangeShortcut[] => {
  const today = dayjs().startOf('day');
  const lastMonth = today.subtract(1, 'month');
  const lastYear = today.subtract(1, 'year');
  return [
    {
      id: 'today',
      label: t('customer_statistics.date_picker_shortcut_today'),
      range: [today, today],
    },
    {
      id: 'yesterday',
      label: t('customer_statistics.date_picker_shortcut_yesterday'),
      range: [today.subtract(1, 'day'), today.subtract(1, 'day')],
    },
    {
      id: 'last7Days',
      label: t('customer_statistics.date_picker_shortcut_last_7_days'),
      range: [today.subtract(6, 'day'), today],
    },
    {
      id: 'last30Days',
      label: t('customer_statistics.date_picker_shortcut_last_30_days'),
      range: [today.subtract(29, 'day'), today],
    },
    {
      id: 'thisMonth',
      label: t('customer_statistics.date_picker_shortcut_this_month'),
      range: [today.startOf('month'), today],
    },
    {
      id: 'lastMonth',
      label: t('customer_statistics.date_picker_shortcut_last_month'),
      range: [lastMonth.startOf('month'), lastMonth.endOf('month')],
    },
    {
      id: 'thisYear',
      label: t('customer_statistics.date_picker_shortcut_this_year'),
      range: [today.startOf('year'), today],
    },
    {
      id: 'lastYear',
      label: t('customer_statistics.date_picker_shortcut_last_year'),
      range: [lastYear.startOf('year'), lastYear.endOf('year')],
    },
  ];
};

interface DateRangePickerProps
  extends Omit<MuiDateRangePickerProps<Dayjs>, 'value' | 'onChange' | 'slots'> {
  dateBoundaries: {
    minDate: dayjs.Dayjs | null;
    maxDate: dayjs.Dayjs | null;
  };
  startDate: string | null;
  setStartDate: (date: string | null) => void;
  endDate: string | null;
  setEndDate: (date: string | null) => void;
}

function StatisticsDateRangePicker({
  dateBoundaries,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  format = 'DD/MM/YYYY',
  disableAutoMonthSwitching = true,
  slotProps,
  localeText,
  shouldDisableDate,
  ...rest
}: DateRangePickerProps) {
  const [datePickerError, setDatePickerError] = useState<string>('');
  /**
   * Debounce the setDates function to avoid multiple calls and peform
   * the date validation, needed when the user writes down the dates
   * in the input
   */
  const debouncedSetDates = useMemo(
    () =>
      debounce((value: [Dayjs | null, Dayjs | null]) => {
        try {
          if (!value[0] && !value[1]) {
            setDatePickerError('');
            setStartDate(null);
            setEndDate(null);
            return;
          }
          if (!value[0] || !value[1]) return;
          const diffInYears = value[1].diff(value[0], 'year', true);
          const isBefore = value[1].isBefore(value[0], 'day');
          if (diffInYears > 1) {
            setDatePickerError(
              t('customer_statistics.date_picker_range_error')
            );
            return;
          } else if (isBefore) {
            setDatePickerError(t('customer_statistics.date_picker_end_error'));
            return;
          } else {
            setDatePickerError('');
          }
          setStartDate(value[0]?.toDate().toISOString());
          setEndDate(value[1]?.toDate().toISOString());
        } catch (error) {
          setDatePickerError(t('customer_statistics.date_picker_error'));
        }
      }, 500),
    [t]
  );

  const onDatePickerChange = useCallback(
    (value: [Dayjs | null, Dayjs | null]) => {
      debouncedSetDates(value);
    },
    [debouncedSetDates]
  );

  const shortcuts = useMemo(() => buildDateRangeShortcuts(), []);

  const isShortcutActive = useCallback(
    (shortcut: DateRangeShortcut) =>
      !!startDate &&
      !!endDate &&
      dayjs(startDate).isSame(shortcut.range[0], 'day') &&
      dayjs(endDate).isSame(shortcut.range[1], 'day'),
    [startDate, endDate]
  );

  const handleShortcutClick = useCallback(
    (range: [Dayjs, Dayjs]) => {
      debouncedSetDates.clear();
      setDatePickerError('');
      const maxDate = dateBoundaries?.maxDate;
      const end = maxDate && range[1].isAfter(maxDate, 'day') ? maxDate : range[1];
      setStartDate(range[0].toDate().toISOString());
      setEndDate(end.toDate().toISOString());
    },
    [debouncedSetDates, dateBoundaries?.maxDate, setStartDate, setEndDate]
  );

  const [isCustomRangeExpanded, setIsCustomRangeExpanded] = useState(false);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.375rem',
        }}
      >
        {shortcuts.map((shortcut) => {
          const isActive = isShortcutActive(shortcut);
          return (
            <Chip
              key={shortcut.id}
              label={shortcut.label}
              size="small"
              clickable
              variant={isActive ? 'filled' : 'outlined'}
              onClick={() => handleShortcutClick(shortcut.range)}
              sx={{
                height: '1.5rem',
                fontSize: '0.6875rem',
                fontWeight: 500,
                borderColor: sgrColors.grey_cloud,
                color: isActive ? '#ffffff' : sgrColors.backdrop,
                backgroundColor: isActive
                  ? sgrColors.navy
                  : 'transparent',
                '&:hover': {
                  backgroundColor: isActive
                    ? sgrColors.navy
                    : sgrColors.chips_background,
                },
              }}
            />
          );
        })}
      </Box>
      <Accordion
        disableGutters
        elevation={0}
        square
        expanded={isCustomRangeExpanded}
        onChange={(_, expanded) => setIsCustomRangeExpanded(expanded)}
        sx={{
          marginTop: '0.5rem',
          backgroundColor: 'transparent',
          border: 'none',
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ fontSize: '1rem' }} />}
          sx={{
            minHeight: 0,
            padding: 0,
            '& .MuiAccordionSummary-content': {
              margin: '0.25rem 0',
            },
          }}
        >
          <Box
            component="span"
            sx={{
              fontSize: '0.6875rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: sgrColors.backdrop,
            }}
          >
            {t('customer_statistics.date_picker_custom_range_label')}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ padding: '0 0 0.25rem' }}>
          <DateRangePicker
            {...rest}
            slots={{ field: SingleInputDateRangeField }}
            value={[
              startDate ? dayjs(startDate) : null,
              endDate ? dayjs(endDate) : null,
            ]}
            format={format}
            onChange={(value) => {
              onDatePickerChange(value);
            }}
            slotProps={{
              ...slotProps,
              field: {
                clearable: true,
                ...slotProps?.field,
              },
              textField: {
                error: datePickerError !== '',
                helperText: datePickerError || '',
                ...slotProps?.textField,
              },
            }}
            disableAutoMonthSwitching={disableAutoMonthSwitching}
            localeText={{
              start: t('customer_statistics.date_picker_start'),
              end: t('customer_statistics.date_picker_end'),
              ...localeText,
            }}
            shouldDisableDate={
              shouldDisableDate ??
              ((date: Dayjs) => {
                const maxEndDate = dayjs(startDate).add(
                  1,
                  'year'
                );
                if (
                  dateBoundaries &&
                  date.isAfter(dayjs(dateBoundaries?.maxDate), 'day')
                ) {
                  return true;
                }
                return date.isAfter(maxEndDate);
              })
            }
          />
        </AccordionDetails>
      </Accordion>
    </LocalizationProvider>
  );
}

export default StatisticsDateRangePicker;
